package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.VisibilityThreshold
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.composables.icons.feather.Feather
import com.composables.icons.feather.Link
import com.composables.icons.feather.Smile
import com.composables.icons.feather.Table
import com.composables.icons.feather.Tool
import com.composables.icons.feather.Type
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Keyboard
import com.composables.icons.lucide.Link
import com.composables.icons.lucide.Redo2
import com.composables.icons.lucide.Smile
import com.composables.icons.lucide.Type
import com.composables.icons.lucide.Undo2
import com.taocent.simple.compose.component.richtext.core.internal.dialog.ExportJsonDialog
import com.taocent.simple.compose.component.richtext.core.internal.dialog.LinkDialog
import com.taocent.simple.compose.component.blockrichtext.internal.dialog.TableInsertDialog
import com.taocent.simple.compose.component.richtext.core.internal.ui.panel.EmojiPanel
import com.taocent.simple.compose.component.richtext.core.internal.ui.panel.TextStylePanel
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbar
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbarHostState
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.LocalFloatingToolbarHost
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.ToolbarCategoryItem
import com.taocent.simple.compose.component.richtext.core.internal.ui.EditorScaffold
import com.taocent.simple.compose.component.richtext.core.internal.ui.EmptyTextContextMenuProvider
import com.taocent.simple.compose.component.richtext.core.internal.ui.EmptyTextToolbar
import com.taocent.simple.compose.component.richtext.core.internal.ui.rememberEditorPanelController
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuDropdownProvider
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuToolbarProvider
import androidx.compose.ui.platform.LocalTextToolbar
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.toAnnotatedStringOrNull
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.toRichAnnotatedStringOrNull
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.clipboardGetText
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.clipboardSetText
import com.taocent.simple.compose.component.richtext.core.LocalRichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextConfig
import com.taocent.simple.compose.component.blockrichtext.internal.block.TextBlockItem
import com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableRenderer
import com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TextBlockRenderer
import com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableBlockRenderer
import com.taocent.simple.compose.component.blockrichtext.internal.registry.BlockRegistry
import com.taocent.simple.compose.component.richtext.core.internal.clipboard.toAnnotatedStringOrNull
import com.taocent.simple.compose.component.richtext.core.internal.ui.RichTextClipboardActions
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.compose.runtime.rememberCoroutineScope
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardNeedsInsets
import kotlin.time.Duration.Companion.milliseconds

private enum class BlockToolCategory {
    EMOJI,
    TEXT_STYLE,
    LINK
}

@Composable
@ExperimentalBlockRichTextApi
fun BlockRichTextEditor(
    state: BlockState,
    modifier: Modifier = Modifier,
    config: RichTextConfig? = null,
) {
    // 调用方未传 config 时,使用 core 内置的 RichTextConfig()(customEmojiIcons
    // 默认指向 :richtext-core.DefaultCustomEmojiIcons),无需再 copy() 注入。
    // 显式传入 config 的调用方应自行合并 DefaultCustomEmojiIcons(如果需要默认图标)。
    val effectiveConfig = config ?: RichTextConfig()
    // 屏蔽系统文本工具栏,用我们自己的全局 FloatingToolbar 代替:
    // - 1.11+ 新机制([LocalTextContextMenuToolbarProvider] / [LocalTextContextMenuDropdownProvider]):
    //   选词/长按/双击/右键会调 `provider.showTextContextMenu(...)`,默认 Android 走系统
    //   ActionMode / iOS 走 EditMenu / Desktop 走 Compose Popup —— 全部用 no-op 屏蔽。
    // - 旧机制([LocalTextToolbar]):KMP 1.11.0 Desktop / iOS / js / wasmJs 仍走这里,作为 fallback。
    CompositionLocalProvider(
        LocalRichTextConfig provides effectiveConfig,
        LocalTextContextMenuDropdownProvider provides EmptyTextContextMenuProvider,
        LocalTextContextMenuToolbarProvider provides EmptyTextContextMenuProvider,
        LocalTextToolbar provides EmptyTextToolbar,
    ) {
        BlockRichTextEditorContent(state, modifier)
    }
}

@Composable
@OptIn(ExperimentalBlockRichTextApi::class)
private fun BlockRichTextEditorContent(
    state: BlockState,
    modifier: Modifier
) {
    val focusRequesterMap = remember { mutableMapOf<String, FocusRequester>() }
    val blockRegistry = remember {
        BlockRegistry().apply {
            registerRenderer(TextBlockRenderer { blockId ->
                focusRequesterMap.getOrPut(blockId) { FocusRequester() }
            })
            registerRenderer(TableBlockRenderer())
        }
    }
    // 焦点管理:
    // - 只在 `focusRequestVersion > 0` 时才调用 `requestFocus()`。
    //   0 是初始值(从未自增),对应 BlockState 初始化时设置 focusedBlockId 而不弹焦点的场景;
    //   自增只发生在用户显式触发的操作中(键盘导航 navGoUp/Down、undo/redo 恢复 Text 光标等)。
    // - 不再监听 `focusedBlockId`,避免因 LazyColumn 滚动/重组让某个 block 首次进入组合时
    //   被动触发 `requestFocus()`,抢走表格单元格焦点并在移动端自动弹出软键盘。
    // - 首次聚焦、点击聚焦等"用户主动操作"已由 TextBlock 内部的 onFocusChanged 自行处理,
    //   不需要由顶层再次强制 requestFocus;只有显式发出的 focus 请求才走顶层。
    val focusRequestVersion = state.focusRequestVersion
    val focusedFocusRequester = focusRequesterMap.getOrPut(state.focusedBlockId) {
        FocusRequester()
    }
    LaunchedEffect(focusRequestVersion) {
        if (focusRequestVersion > 0) {
            focusedFocusRequester.requestFocus()
        }
    }
    // guard 冷却清除：每次 guard 被 SET 或 onUserValueChange 回退（bump）时
    // guardCooldownVersion 自增，LaunchedEffect 重启 300ms delay。
    // 长按期间 onUserValueChange 不断回退→自增→delay 永不触发。
    // 松手 300ms 后自动清除，或按任意非 Backspace 键立即清除。
     LaunchedEffect(state.guardCooldownVersion) {
         if (state.backspaceBlockDeleteGuard) {
             delay(300.milliseconds)
             state.backspaceBlockDeleteGuard = false
             println("[BS_GUARD] COOLDOWN cleared (timeout)")
         }
     }
    fun restoreFocus() {
        val cellRequester = state.focusedTableCellFocusRequester
        if (cellRequester != null) {
            cellRequester.requestFocus()
        } else {
            focusedFocusRequester.requestFocus()
        }
    }
    /**
     * 软键盘/焦点适配入口:**只在用户曾经主动 focus 过任意 RichTextTextField 时**才真正
     * 调用 [FocusRequester.requestFocus]。
     *
     * 适用场景:emoji 插入、TextStyle 按钮回调、hyperlink / 表格 / 导出 dialog 关闭后。
     *
     * 原因:
     * - Android / iOS 移动端:[FocusRequester.requestFocus] 会让系统拉起软键盘
     *   (Activity 配 `windowSoftInputMode=adjustNothing` + ComposeUIViewController 配
     *   `OnFocusBehavior.DoNothing` 也无法完全避免 `requestFocus` 触发 IME 弹起)。
     * - Web 端(mobile webview):`requestFocus` 会让 canvas 获得焦点,触发浏览器
     *   `compositionstart` 弹软键盘;已展开时还会触发 hide→show 闪烁。
     *
     * 因此"被动"路径(表情插入、dialog 关闭)只有在用户之前**已经** focus 过文本块或
     * 表格单元格时,才允许重新恢复焦点 — 此时用户意图明确,即使再弹一次软键盘也合理。
     * 反之,如果用户从未 focus 过任何字段,这些路径就不该无端拉起键盘。
     *
     * 用户**显式**触发键盘的路径(工具栏"键盘"按钮的"展开"分支)应继续使用 [restoreFocus],
     * 因为那就是用户期望的"拉起键盘"动作。
     */
    fun restoreFocusIfHadFocus() {
        if (state.hasUserFocusedAnyField) {
            restoreFocus()
        }
    }
    val haveIme = rememberKeyboardNeedsInsets()
    val clipboard = LocalClipboard.current
    val coroutineScope = rememberCoroutineScope()
    val keyboardController = LocalSoftwareKeyboardController.current
    var activeCategory by remember { mutableStateOf<BlockToolCategory?>(null) }
    var showLinkDialog by remember { mutableStateOf(false) }
    var showExportDialog by remember { mutableStateOf(false) }
    var showTableInsertDialog by remember { mutableStateOf(false) }
    var restoreKeyboardAfterDialog by remember { mutableStateOf(false) }
    val config = LocalRichTextConfig.current
    // 工具栏 / 面板的高度与状态机 — 与 RichTextEditor 共享同一份 :richtext-core 实现。
    val panelController = rememberEditorPanelController(config.panel.defaultHeight)

    LaunchedEffect(panelController.isKeyboardVisible) {
        if (panelController.isKeyboardVisible) {
            activeCategory = null
        }
    }

    // 全局浮动工具栏宿主:每块 / 每格的 RichTextTextField 通过 LocalFloatingToolbarHost
    // 上报选区状态,顶层只渲染一个 FloatingToolbar。
    val floatingToolbarHost = remember { FloatingToolbarHostState() }

    CompositionLocalProvider(LocalFloatingToolbarHost provides floatingToolbarHost) {
        Column(modifier = modifier.onPreviewKeyEvent { event ->
            if (event.type == KeyEventType.KeyDown && (event.isCtrlPressed || event.isMetaPressed) && event.key == Key.V) {
                // 顶层统一拦截 Cmd/Ctrl+V,避免双重粘贴:
                //
                // 根因:Web 端收到 Cmd/Ctrl+V 时浏览器**同时**派发两个独立事件:
                //   1. `keydown` event → Compose key system → 本 onPreviewKeyEvent
                //   2. 原生 `paste` event → BasicTextField(基于 contentEditable)内部
                //      处理 → 走 IME 路径插入 plain text
                // 如果不在路径 1 统一处理 paste,路径 2 就会与路径 1 的 paste 叠加,
                // 变成「A[无样式]A[有样式]」重复粘贴。
                //
                // 修复:**双层防御** + 主动 paste 还原 spans + emoji:
                //   - 设 `state.suppressNextNativePasteEvent = true`(BlockState 级别):
                //     web 端 [InterceptImePasteTextInput] 注册的 `paste` event capture
                //     listener 在 capture 阶段 `stopImmediatePropagation()`,**根本不让
                //     paste event 到达 BasicTextField 内部**。这是 web 端主防线 ——
                //     web 端 `BasicTextField` 内部 native paste 路径不调 `onValueChange`
                //     回调(直接通过 `PlatformTextInput` 更新 state),所以下面的
                //     `RichTextState.onValueChange` 拦截在 web 端**不生效**,必须靠
                //     DOM 级别阻止。
                //   - 设 target block 的 RichTextState flag(冗余防线,适用于 iOS /
                //     旧 Android 等走 `onValueChange` 回调的平台):被
                //     [RichTextState.onValueChange] 消费,跳过 `textEditor.onValueChange`
                //     的默认 IME 路径,避免 plain text 重复插入。
                //   - 主动调 `clipboardActions.paste()` 走 `insertAnnotatedString`
                //     还原 spans + emoji shortcode → `insertTextWithEmojis` 还原 emoji。
                //     普通文本无 rich styles 时则 fallback 到 `state.insertText`。
                //   - 剪贴板是 Block JSON 时走 `state.pasteJson` 整体替换 blocks。
                //
                // 同步部分先设 flag 后 return true;剪贴板读取 + paste 是 suspend,
                // 在 coroutine 中完成,顺序保证 flag 早于后续 native paste event
                // 派发可见(同一 UI 线程顺序执行)。
                state.suppressNextNativePasteEvent = true
                // 解析"实际当前焦点 RichTextState":表格单元格焦点走
                // [BlockState.focusedTableCellState],文本块走 [BlockState.focusedBlockId]
                // 对应的 blockState。**关键**:`focusedBlockId` 在 cell focus 期间
                // 仍指向**上次**的 TextBlock(只有 `setTextCursor` 才更新它),所以不能
                // 直接用 `getBlockState(focusedBlockId)`,否则会粘贴到 TextBlock 而不是 cell。
                val targetRichTextState = resolveActiveRichTextState(state)
                if (targetRichTextState != null) {
                    targetRichTextState.markSuppressedImePasteOnNextValueChange()
                }
                coroutineScope.launch {
                    // **web 端只读一次**:`navigator.clipboard.read()` 走 IPC + permission check
                    // 是 web 端 paste 性能的最大头。这里读取一次,后面 Block JSON 路径
                    // 直接用 clipText,普通 paste 路径通过 [RichTextClipboardActions.paste]
                    // 的 preloaded 重载传入,跳过内部 `clipboardGetText()` 第二次读取。
                    val entry = clipboard.getClipEntry() ?: return@launch
                    // **优先读 text/html 副 MIME**(JVM / Web / Android 平台写入端会带
                    // `public.html` / `fragmentHtmlFlavor` / `text/html`),让跨 app 粘贴
                    // 也能还原 spans / paragraph / emoji / hyperlink。无 text/html 时
                    // fallback 到 text/plain。
                    val clipAnnotated =
                        entry.toRichAnnotatedStringOrNull() ?: entry.toAnnotatedStringOrNull()
                            ?: return@launch
                    val clipText = clipAnnotated.text
                    if (clipText.isEmpty()) return@launch
                    if (state.isBlockJson(clipText)) {
                        // 优先级 1:Block JSON → 整体替换 blocks
                        // 顶层已用 isBlockJson 验证,见 pasteJson KDoc
                        state.pasteJson(clipText)
                    } else {
                        // 优先级 2:普通 paste → 复用 clipboardActions 路径
                        // (applyPreloadedPaste 走 text/html 反序列化 / emoji shortcode 还原 spans + emoji)
                        // **必须用 cell-aware resolveActiveRichTextState**,否则 cell
                        // 焦点下会粘贴到上次 TextBlock(参见上面 targetRichTextState 注释)。
                        val targetState = resolveActiveRichTextState(state)
                        if (targetState != null) {
                            val blockClipboardActions = RichTextClipboardActions(
                                clipboard = clipboard,
                                state = targetState,
                                config = config,
                            )
                            // **传入 preloaded 跳过第二次剪贴板读取**(web 端关键优化)
                            blockClipboardActions.paste(
                                allowSmartPaste = true,
                                preloadedClipAnnotated = clipAnnotated,
                            )
                        }
                    }
                }
                return@onPreviewKeyEvent true
            }
        // editor-level guard: del non-text block, all backspaces go through BasicTextField →
        // onUserValueChange will revert with tryHandleTextBlockValueChangeBeforeApply.
        // Cooldown (300ms) automatically clears after release; non-bs key also clears faster.
        if (event.type == KeyEventType.KeyDown && event.key == Key.Backspace) {
            if (state.backspaceBlockDeleteGuard) {
                state.bumpGuardCooldown()
            }
        }
        if (event.type == KeyEventType.KeyDown && event.key != Key.Backspace) {
            if (state.backspaceBlockDeleteGuard) {
                state.backspaceBlockDeleteGuard = false
            }
        }
        if (event.type == KeyEventType.KeyDown && state.navRowAnchorTableId != null) {
            val rowPosition = (state.editorCursor as? com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.RowAnchor)
                ?.position
            val isRowAfter = rowPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After
            when (event.key) {
                Key.DirectionUp -> {
                    state.navRowGoUp()
                    return@onPreviewKeyEvent true
                }
                Key.DirectionDown -> {
                    state.navRowGoDown()
                    return@onPreviewKeyEvent true
                }
                Key.DirectionLeft -> {
                    if (event.isShiftPressed && isRowAfter) {
                        return@onPreviewKeyEvent state.navRowEnterLastCell()
                    }
                    return@onPreviewKeyEvent state.navRowGoLeft()
                }
                Key.DirectionRight -> {
                    if (event.isShiftPressed && !isRowAfter) {
                        return@onPreviewKeyEvent state.navRowEnterFirstCell()
                    }
                    return@onPreviewKeyEvent state.navRowGoRight()
                }
                Key.Enter -> {
                    return@onPreviewKeyEvent state.navRowSplitOrNewline()
                }
                Key.Tab -> {
                    val isRowAfter = rowPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After
                    return@onPreviewKeyEvent if (isRowAfter) state.navRowInsertRowBelow() else state.navRowInsertRowAbove()
                }
                Key.Backspace, Key.Delete -> {
                    return@onPreviewKeyEvent state.navRowDeleteRow()
                }
            }
        }
        if (event.type == KeyEventType.KeyDown && state.navSelectedTableId != null) {
            // 锚点态分 Before(左高亮)/ After(右高亮)两种,Enter / Backspace 语义不同:
            //   - Before(navInsertBeforeTable / navDeleteFromAbove):作用对象 = 锚点表 **上方** 块
            //   - After (navInsertAfterTable  / 删除保护):           作用对象 = 锚点表 **下方** 块
            //     特别地,Backspace/Delete 在 After 态下 **吞掉事件不做删除**,
            //     避免误删锚点表本身或下方文本块(用户语义是"高亮表格当前受保护")。
            val anchorPosition = (state.editorCursor as? com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.BlockAnchor)
                ?.position
            val isAfter = anchorPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After
            when (event.key) {
                Key.DirectionDown -> {
                    state.navGoDown()
                    return@onPreviewKeyEvent true
                }
                Key.DirectionUp -> {
                    state.navGoUp()
                    return@onPreviewKeyEvent true
                }
                Key.DirectionLeft -> {
                    // Shift+← 优先处理:After(右高亮)穿透进入当前表格末行末列 cell。
                    // 非 Shift 的 ← 保持锚点左右穿梭语义。
                    if (event.isShiftPressed && isAfter) {
                        return@onPreviewKeyEvent state.navEnterLastCellFromAfter()
                    }
                    // ← 优先级:
                    //   - After(右高亮)+ ← :同表切换到 Before(左高亮),形成"右→左"翻面。
                    //   - Before(左高亮)+ ← :上方是 TextBlock 落末行末位;否则若上方是 TableBlock,
                    //     跳到上一张表的 After(右高亮),形成连续两表锚点链。
                    return@onPreviewKeyEvent if (isAfter) {
                        state.navToggleAnchorPosition()
                    } else {
                        state.navGoLeftToPrevText() || state.navGoLeftToPrevTableAfter()
                    }
                }
                Key.DirectionRight -> {
                    // Shift+→ 优先处理:Before(左高亮)穿透进入当前表格首行首列 cell。
                    // 非 Shift 的 → 保持锚点左右穿梭语义。
                    if (event.isShiftPressed && !isAfter) {
                        return@onPreviewKeyEvent state.navEnterFirstCellFromBefore()
                    }
                    // → 优先级,与 ← 完全镜像:
                    //   - Before(左高亮)+ → :同表切换到 After(右高亮)。
                    //   - After (右高亮)+ → :下方是 TextBlock 落首行首位;否则若下方是 TableBlock,
                    //     跳到下一张表的 Before(左高亮)。
                    return@onPreviewKeyEvent if (!isAfter) {
                        state.navToggleAnchorPosition()
                    } else {
                        state.navGoRightToNextText() || state.navGoRightToNextTableBefore()
                    }
                }
                Key.Enter -> {
                    return@onPreviewKeyEvent if (isAfter) {
                        state.navInsertAfterTable()
                    } else {
                        state.navInsertBeforeTable()
                    }
                }
                Key.Backspace, Key.Delete -> {
                    return@onPreviewKeyEvent if (isAfter) {
                        // After(右高亮)态:走"删除选中保护"二段式 —— 转 selectedTableBlockId,
                        // 让 TableRenderer 切到整表外框高亮(并启动 selectionAutoClearDelayMs 自动清除);
                        // 用户在窗口内再按一次 Backspace,会命中下方 selectedTableBlockId != null 分支,
                        // 调 deleteSelectedTable() 真正删表。
                        state.navConvertToSelectedTable()
                    } else {
                        state.navDeleteFromAbove()
                    }
                }
            }
        }
        if (event.type == KeyEventType.KeyDown && state.selectedTableBlockId != null) {
            if (event.key == Key.Backspace) {
                state.deleteSelectedTable()
                state.backspaceBlockDeleteGuard = true
                state.bumpGuardCooldown()
                println("[BS_GUARD] SET true (BlockRichTextEditor: selectedTable deleted=${state.selectedTableBlockId})")
                state.navRestoreIfPending()
                return@onPreviewKeyEvent true
            }
        }
        false
    }) {
        LazyColumn(modifier = Modifier.weight(1f).fillMaxWidth()) {
            items(state.blocks, key = { it.id }) { block ->
            // 块级插入/删除平滑动画:
            // - fade in / fade out:220ms FastOutSlowInEasing,与项目"工具栏/面板 220ms"约束一致
            // - placement(块上下位移):无回弹 spring,跟 `animatedPanelHeight` 的
            //   `spring(DampingRatioNoBouncy, StiffnessMediumLow)` 风格统一
            // - 关键前提:每个 block 的 `key = { it.id }` 必须稳定,否则
            //   LazyColumn 会把"修改"误判为"先删除旧再插入新",无法播 placement 动画
            // - 不影响焦点:`focusRequestVersion` 自增时 `LaunchedEffect` 调用
            //   `requestFocus()`,目标 block 即使在 fade in 中,FocusRequester
            //   已在 composable 内 remember,内部会 await 目标 view 出现
            val itemModifier = Modifier.animateItem(
                fadeInSpec = tween(durationMillis = 220, easing = FastOutSlowInEasing),
                placementSpec = spring(
                    dampingRatio = Spring.DampingRatioNoBouncy,
                    stiffness = Spring.StiffnessMediumLow,
                    visibilityThreshold = IntOffset.VisibilityThreshold,
                ),
                fadeOutSpec = tween(durationMillis = 220, easing = FastOutSlowInEasing),
            )
            blockRegistry.render(block, state, itemModifier)
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .pointerInput(Unit) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent(PointerEventPass.Initial)
                            if (event.type == PointerEventType.Press) {
                                state.saveSelection()
                            }
                        }
                    }
                }
        ) {
            EditorScaffold(
                onUserInteraction = { state.saveSelection() },
                panelController = panelController,
                toolbar = {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .focusProperties { canFocus = false }
                            .background(MaterialTheme.colorScheme.surface)
                    ) {
                        Row(
                            modifier = Modifier
                                .weight(1f)
                                .horizontalScroll(rememberScrollState())
                        ) {
                            ToolbarCategoryItem(
                                icon = { Image(Lucide.Undo2, contentDescription = "撤销") },
                                isActive = false,
                                enabled = state.canUndo,
                                onClick = { state.undo() }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Lucide.Redo2, contentDescription = "重做") },
                                isActive = false,
                                enabled = state.canRedo,
                                onClick = { state.redo() }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Feather.Smile, contentDescription = null) },
                                isActive = activeCategory == BlockToolCategory.EMOJI,
                                onClick = {
                                    if (activeCategory == BlockToolCategory.EMOJI) {
                                        activeCategory = null
                                    } else {
                                        panelController.requestExpand()
                                        if (panelController.isKeyboardVisible) keyboardController?.hide()
                                        activeCategory = BlockToolCategory.EMOJI
                                    }
                                }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Feather.Type, contentDescription = null) },
                                isActive = activeCategory == BlockToolCategory.TEXT_STYLE,
                                onClick = {
                                    if (activeCategory == BlockToolCategory.TEXT_STYLE) {
                                        activeCategory = null
                                    } else {
                                        panelController.requestExpand()
                                        if (panelController.isKeyboardVisible) keyboardController?.hide()
                                        activeCategory = BlockToolCategory.TEXT_STYLE
                                    }
                                }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Feather.Link, contentDescription = null) },
                                isActive = false,
                                onClick = {
                                    restoreKeyboardAfterDialog = panelController.isKeyboardVisible
                                    showLinkDialog = true
                                }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Feather.Table, contentDescription = null) },
                                isActive = false,
                                onClick = {
                                    restoreKeyboardAfterDialog = panelController.isKeyboardVisible
                                    showTableInsertDialog = true
                                }
                            )
                            ToolbarCategoryItem(
                                icon = { Image(Feather.Tool, contentDescription = null) },
                                isActive = false,
                                onClick = {
                                    restoreKeyboardAfterDialog = panelController.isKeyboardVisible
                                    showExportDialog = true
                                }
                            )
                        }
                        ToolbarCategoryItem(
                            icon = { Image(Lucide.Keyboard, contentDescription = "键盘") },
                            isActive = false,
                            onClick = {
                                if (panelController.isKeyboardVisible) {
                                    panelController.requestKeyboardCollapse()
                                    keyboardController?.hide()
                                } else {
                                    panelController.requestKeyboardExpand(
                                        hasActiveCategory = activeCategory != null
                                    )
                                    restoreFocus()
                                    keyboardController?.show()
                                }
                            }
                        )
                    }
                },
                panel = { controller ->
                    val animatedPanelHeight = controller.animatedPanelHeight(
                        hasActiveCategory = activeCategory != null
                    )
                    Surface(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(animatedPanelHeight),
                        color = MaterialTheme.colorScheme.surfaceContainerLow,
                        tonalElevation = 2.dp
                    ) {
                        Crossfade(
                            targetState = activeCategory,
                            animationSpec = tween(durationMillis = config.panel.crossfadeAnimationMs)
                        ) { category ->
                            when (category) {
                                BlockToolCategory.EMOJI -> {
                                    val emojiSizeSp = state.currentSpanStyle().fontSize.let {
                                        if (it != TextUnit.Unspecified) it else 16.sp
                                    }
                                    EmojiPanel(
                                        onEmojiSelected = { emoji ->
                                            state.restoreSavedSelection()
                                            state.insertText(emoji)
                                            // 软键盘适配:仅当用户曾经 focus 过任意字段时才恢复焦点
                                            // (修复 web 移动端 + Android/iOS 在无焦点/键盘收起态
                                            // 点 emoji 会无端弹起软键盘的问题)。
                                            if (!haveIme)restoreFocusIfHadFocus()
                                        },
                                        onCustomEmojiSelected = { emojiId ->
                                            state.restoreSavedSelection()
                                            state.insertCustomEmoji(emojiId, emojiSizeSp)
                                            if (!haveIme)restoreFocusIfHadFocus()
                                        }
                                    )
                                }
                                BlockToolCategory.TEXT_STYLE -> TextStylePanel(
                                    state = state,
                                    restoreSelection = { state.restoreSavedSelection() },
                                    // 同上:样式按钮被点时,只在用户原本就 focus 过时才恢复焦点
                                    onActionCompleted = { if (!haveIme)restoreFocusIfHadFocus() }
                                )
                                else -> {}
                            }
                        }
                    }
                }
            )
        }
    }

    if (showLinkDialog) {
        LinkDialog(
            state = state,
            restoreSelection = { state.restoreSavedSelection() },
            onDismiss = { showLinkDialog = false },
            onConfirm = { showLinkDialog = false }
        )
    }

    var linkDialogEverShown by remember { mutableStateOf(false) }
    if (showLinkDialog) {
        linkDialogEverShown = true
    }

    LaunchedEffect(showLinkDialog) {
        if (!showLinkDialog && linkDialogEverShown) {
            val shouldRestoreKeyboard = restoreKeyboardAfterDialog
            restoreKeyboardAfterDialog = false
            // 软键盘/焦点适配:
            // - 原条件 `shouldRestoreKeyboard` 来自 `panelController.isKeyboardVisible`,
            //   即只在软键盘"可见"时才恢复焦点。问题是:用户可能在"软键盘本就未展开"
            //   的情况下打开 hyperlink dialog(比如在 Desktop / Web / iPad 拆分键盘 / 桌面 web),
            //   关闭后焦点不恢复,文本块/单元格光标就丢了。
            // - 改为:用户**曾经** focus 过任意 RichTextTextField → 关闭后恢复焦点
            //   (不弹键盘,因为之前键盘就没弹);用户曾 focus 过 且 软键盘本就可见 →
            //   恢复焦点 + 弹键盘。这样:
            //   - web 移动端:无键盘状态下点 hyperlink,关闭后焦点回到文本块,不弹键盘
            //   - Android/iOS:同上,且键盘本就可见时仍保留"恢复键盘"行为
            // - 用户**从未** focus 过任何字段 → 什么也不做(没有焦点可恢复)
            if (state.hasUserFocusedAnyField) {
                delay(config.panel.linkDialogFocusRestoreDelayMs)
                restoreFocus()
                if (shouldRestoreKeyboard) {
                    keyboardController?.show()
                }
            }
        }
    }

    if (showExportDialog) {
        val jsonContent = remember { state.toJson() }
        ExportJsonDialog(
            jsonContent = jsonContent,
            onDismiss = { showExportDialog = false }
        )
    }

    var exportDialogEverShown by remember { mutableStateOf(false) }
    if (showExportDialog) {
        exportDialogEverShown = true
    }

    LaunchedEffect(showExportDialog) {
        if (!showExportDialog && exportDialogEverShown) {
            val shouldRestoreKeyboard = restoreKeyboardAfterDialog
            restoreKeyboardAfterDialog = false
            if (state.hasUserFocusedAnyField) {
                delay(config.panel.linkDialogFocusRestoreDelayMs)
                restoreFocus()
                if (shouldRestoreKeyboard) {
                    keyboardController?.show()
                }
            }
        }
    }

    if (showTableInsertDialog) {
        TableInsertDialog(
            onDismiss = {
                showTableInsertDialog = false
            },
            onConfirm = { rows, columns ->
                state.insertTable(rows, columns)
                showTableInsertDialog = false
            }
        )
    }

    var tableDialogEverShown by remember { mutableStateOf(false) }
    if (showTableInsertDialog) {
        tableDialogEverShown = true
    }

    LaunchedEffect(showTableInsertDialog) {
        if (!showTableInsertDialog && tableDialogEverShown) {
            val shouldRestoreKeyboard = restoreKeyboardAfterDialog
            restoreKeyboardAfterDialog = false
            if (state.hasUserFocusedAnyField) {
                delay(config.panel.linkDialogFocusRestoreDelayMs)
                restoreFocus()
                if (shouldRestoreKeyboard) {
                    keyboardController?.show()
                }
            }
        }
    }

    // 全局唯一浮动工具栏:只在有活动 RichTextTextField 上报选区时渲染。
    val activeToolbar = floatingToolbarHost.active
    if (activeToolbar != null) {
        val toolbarState = activeToolbar.state
        val richTextClipboard = remember(toolbarState, clipboard) {
            object {
                suspend fun setText(text: AnnotatedString) =
                    clipboardSetText(clipboard, toolbarState, text)
                suspend fun getText(): AnnotatedString? =
                    clipboardGetText(clipboard, toolbarState).getOrNull()
            }
        }
        FloatingToolbar(
            state = toolbarState,
            textFieldGlobalPosition = activeToolbar.position,
            textFieldHeightPx = activeToolbar.heightPx,
            anchor = activeToolbar.anchor,
            showToolbar = !activeToolbar.isDragging,
            onDismiss = { floatingToolbarHost.dismiss() },
            onCopy = {
                val sel = toolbarState.textFieldValue.selection
                if (!sel.collapsed) {
                    val selected = toolbarState.textFieldValue.annotatedString
                        .subSequence(sel.min, sel.max)
                    coroutineScope.launch { richTextClipboard.setText(selected) }
                }
                // 工具栏按钮被点击后主动收起浮动工具栏,并在 selection 不变时不再自动重新显示
                // (用户报告"再点一次要收起浮动工具栏" — 切样式/复/剪/粘/全按钮后立即收起)
                toolbarState.dismissToolbar()
            },
            onCut = {
                val sel = toolbarState.textFieldValue.selection
                if (!sel.collapsed) {
                    val selected = toolbarState.textFieldValue.annotatedString
                        .subSequence(sel.min, sel.max)
                    coroutineScope.launch { richTextClipboard.setText(selected) }
                    toolbarState.deleteSelection()
                }
                toolbarState.dismissToolbar()
            },
            onPaste = {
                // 跟 Cmd/Ctrl+V 路径完全一致(同 `state.isBlockJson` + `state.pasteJson`
                // + `blockClipboardActions.paste(preloadedClipAnnotated = ...)` 链路),
                // 去掉 `tryStartUserGestureClipboardRead + richTextClipboard.getText +
                // executePreloadedPaste` 包装 — 那一套包装在 click 异步 coroutine 中
                // 会让协程在 `userGestureReadDeferred.await()` yield 期间被 `dismissToolbar`
                // 触发的 Composable 重组 cancel,导致 paste 完全不执行。
                // 不调 `state.suppressNextNativePasteEvent` / `markSuppressedImePasteOnNextValueChange`
                // —— 这两个是 web 端 native paste event 抑制用,浮动工具栏按钮 click
                // 不会触发原生 paste event,不需要。
                coroutineScope.launch {
                    val entry = clipboard.getClipEntry() ?: return@launch
                    val clipAnnotated = entry.toRichAnnotatedStringOrNull()
                        ?: entry.toAnnotatedStringOrNull() ?: return@launch
                    val clipText = clipAnnotated.text
                    if (clipText.isEmpty()) return@launch
                    if (state.isBlockJson(clipText)) {
                        state.pasteJson(clipText)
                    } else {
                        // 必须 cell-aware:cell 焦点下不能 fallback 到 focusedBlockId(指向
                        // 上次 TextBlock),否则粘贴会写入 TextBlock 而不是 cell(参见上面
                        // Cmd/Ctrl+V 路径的 resolveActiveRichTextState 注释)。
                        val targetState = resolveActiveRichTextState(state)
                        if (targetState != null) {
                            val blockClipboardActions = RichTextClipboardActions(
                                clipboard = clipboard,
                                state = targetState,
                                config = config,
                            )
                            blockClipboardActions.paste(
                                allowSmartPaste = true,
                                preloadedClipAnnotated = clipAnnotated,
                            )
                        }
                    }
                }
                toolbarState.dismissToolbar()
            },
            onSelectAll = {
                toolbarState.selectAll()
                toolbarState.dismissToolbar()
            }
        )
    }
    }  // 闭合 CompositionLocalProvider
}

@Composable
@ExperimentalBlockRichTextApi
fun rememberBlockState(
    maxUndoHistory: Int = 100,
    undoMergeIntervalMs: Long = 500
): BlockState {
    return remember {
        BlockState(
            maxUndoHistory = maxUndoHistory,
            undoMergeIntervalMs = undoMergeIntervalMs
        )
    }
}
